/**
 * Pure helpers for the reprint queue page (P5). Kept out of the component so the rules that decide
 * what the operator SEES — how old a garment is, what it will cost to print, what order it prints in
 * — are unit-testable without a DOM.
 */
import { reprintPlacementsFor } from '@fsp/contracts';
import type { components } from '@/lib/api/types.gen';

/** The entry schema is inlined inside the list response by the generator, so index into it rather
 *  than hand-writing a duplicate type that would silently drift from the contract. */
export type ReprintQueueEntry =
  components['schemas']['ReprintQueueListResponse']['entries'][number];

/**
 * Owner decision D5: amber at 24h, red at 72h, and NEVER auto-expire. A silently expiring entry
 * un-blocks its order, which is the exact failure this whole design exists to prevent — so age is a
 * nag, never an action.
 */
export const AGING_HOURS = 24;
export const STALE_HOURS = 72;

export type QueueAgeTone = 'fresh' | 'aging' | 'stale';

export function queueAge(addedAt: string, nowMs: number): { hours: number; tone: QueueAgeTone } {
  const hours = Math.max(0, (nowMs - new Date(addedAt).getTime()) / 3_600_000);
  const tone: QueueAgeTone =
    hours >= STALE_HOURS ? 'stale' : hours >= AGING_HOURS ? 'aging' : 'fresh';
  return { hours, tone };
}

/** "3h" / "2d" — deliberately coarse; the exact minute of a defect report is not a decision input. */
export function ageLabel(hours: number): string {
  if (hours < 1) return 'just now';
  if (hours < 24) return `${Math.floor(hours)}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Oldest first — the same order the server packs sheets in, so the list reads as the print order.
 * `sk` breaks ties so two garments reported in the same second never swap places between renders.
 */
export function sortQueue(entries: readonly ReprintQueueEntry[]): ReprintQueueEntry[] {
  return [...entries].sort(
    (a, b) => a.addedAt.localeCompare(b.addedAt) || a.sk.localeCompare(b.sk),
  );
}

/**
 * What the operator is about to spend, computed CLIENT-SIDE for the button label only.
 *
 * ⚠️ Advisory. The authoritative number comes back from the server's confirmation refusal, which
 * knows the live roll, the per-sheet cap and the store split. The two can legitimately differ — most
 * obviously on blanks: `transferOnly` is ANDed per LINE, so two garments of one line where only one
 * is transfer-only still draw two blanks. Showing a number here that the confirm dialog then
 * contradicts would be worse than showing none, so this deliberately mirrors the server's rule
 * rather than counting flags.
 */
export function estimateCost(entries: readonly ReprintQueueEntry[]): {
  garments: number;
  transfers: number;
  replacementBlanks: number;
  /** How many garments will be reprinted at only SOME of their prints. Drives the confirm dialog's
   *  narrowing hint — inferring it from an arithmetic range instead both fires when nothing was
   *  narrowed and stays silent when everything was (a review find). */
  narrowedGarments: number;
} {
  let replacementBlanks = 0;
  let transfers = 0;
  let narrowedGarments = 0;
  for (const rows of groupByRun(entries).values()) {
    // THE scope decision, the same function the server's quote and build run — so the narrowing a
    // cutter chose is reflected here rather than recomputed with a second, drifting rule. It also owns
    // the `transferOnly` AND: one scorched blank in a line forces the draw for all of that line's
    // garments on the sheet.
    const scope = reprintPlacementsFor(rows);
    if (!scope.transferOnly) replacementBlanks += rows.length;
    // Films = placements × garments, mirroring the packer's `placementCount * take`.
    transfers += scope.placements.length * rows.length;
    if (scope.narrowed) narrowedGarments += rows.length;
  }
  return { garments: entries.length, transfers, replacementBlanks, narrowedGarments };
}

/** One order line, across every production run of it. What the cross-run review rule is about. */
export const lineKeyOf = (e: ReprintQueueEntry): string => `${e.storeId}#${e.orderId}#${e.lineId}`;

/**
 * The key the reprint scope and the cost are decided on: one line WITHIN one production run.
 *
 * ⚠️ Must match the server's grouping exactly (`reprint-flush.ts` `groupKeyOf`), which became per
 * (line, run) when the cohort segment went into the queue SK. Grouping by line alone here — as this file
 * did — makes both mirrors LIE the moment a line has two runs queued: `transferOnly` would be ANDed
 * across runs the server treats separately, `ruinedLocations` unioned across them, so the "front only"
 * badge can name a film neither sheet carries and `transfers` double-counts (2 placements × 2 rows = 4
 * where the server plans 1 + 1). The confirm dialog then contradicts the authoritative server quote it
 * sits next to.
 *
 * `?? ''` mirrors the server's legacy fallback, so pre-cohort rows group exactly as they always did.
 */
export const runKeyOf = (e: ReprintQueueEntry): string => `${lineKeyOf(e)}#${e.cohortKey ?? ''}`;

function groupByRun(entries: readonly ReprintQueueEntry[]): Map<string, ReprintQueueEntry[]> {
  const byRun = new Map<string, ReprintQueueEntry[]>();
  for (const e of entries) {
    const arr = byRun.get(runKeyOf(e));
    if (arr) arr.push(e);
    else byRun.set(runKeyOf(e), [e]);
  }
  return byRun;
}

/**
 * The reprint scope PER (LINE, RUN), for rendering. Keyed by `runKeyOf`.
 *
 * ⚠️ Must not be computed per ENTRY. The scope is a group-level decision — any one garment of the group
 * drawing a blank, or reported without naming a film, forces the full set for ALL of them. A badge
 * derived from one entry therefore claims "back only" for a garment the sheet will carry both films
 * for, which is the queue page contradicting the sheet it is about to print (a review find).
 *
 * ⚠️ And not per LINE either, any more: two runs of one line are two sheets with two independent scopes,
 * so a line-wide answer would claim a narrowing neither sheet honours. See `runKeyOf`.
 *
 * Computed over every entry of the group in the list, which is the WIDEST answer: deselecting entries
 * can only remove constraints, so the real flush can narrow further but never widen past this. Erring
 * wide is the safe direction for a label about film.
 */
export function scopeByLine(
  entries: readonly ReprintQueueEntry[],
): Map<string, ReturnType<typeof reprintPlacementsFor>> {
  const out = new Map<string, ReturnType<typeof reprintPlacementsFor>>();
  for (const [key, rows] of groupByRun(entries)) out.set(key, reprintPlacementsFor(rows));
  return out;
}

/** Human name for a garment: "#1001 · Tee · garment 3 of 5". */
export function garmentLabel(e: ReprintQueueEntry): string {
  return `${e.orderName ?? e.orderId} · garment ${e.owedRank} of ${e.batchedQty}`;
}

/**
 * Does this returned row need a human to look at it before it goes back on film?
 *
 * TRUE means "a replacement for this garment has already been committed to film once, so printing it
 * again spends a second film, a second cure cycle and a second blank on one shirt" — the reprint-queue
 * duplicate leak. The page uses it to leave the row OUT of the default selection, which is the last
 * gate before that spend (`reprint-queue-page.tsx` defaults selection to everything waiting).
 *
 * ⚠️ Read the MONOTONE marker first, not just the reason. `returnedReason` is last-write, so a row that
 * came back ALREADY_REPRINTED, was deliberately re-flushed, and then had that second sheet deleted
 * before printing is re-stamped SHEET_GONE — and keying only off the reason would silently restore the
 * pre-ticked default for a garment that has already eaten a film.
 *
 * ⚠️ A row that came back with NO reason is UNKNOWN, and unknown resolves toward review, not toward
 * printing: a needless tick costs a human two seconds, a needless film is unrecoverable. Only an
 * explicit `SHEET_GONE` with no marker is safe to pre-select. A row that never came back at all
 * (`returnedFromBatchId` absent) is ordinary fresh work and is not under review.
 */
export function returnedNeedsReview(
  e: Pick<ReprintQueueEntry, 'returnedFromBatchId' | 'returnedReason' | 'alreadyReprintedOnce'>,
): boolean {
  if (e.alreadyReprintedOnce === true) return true;
  if (!e.returnedFromBatchId) return false;
  return e.returnedReason !== 'SHEET_GONE';
}

/**
 * Honest copy for the "came back" badge, or null when the row never came back.
 *
 * The previous copy was the single string "Came back from a deleted sheet", which LIED on the commonest
 * path: `returnedFromBatchId` is stamped by the mark-printed fall-through too, where the sheet printed
 * and nothing was deleted. Each branch below now says only what its own stamp proves.
 */
export function returnedBadge(
  e: Pick<
    ReprintQueueEntry,
    'returnedFromBatchId' | 'returnedReason' | 'alreadyReprintedOnce' | 'sourceBatchSeq'
  >,
): { label: string; title: string; needsReview: boolean } | null {
  if (!e.returnedFromBatchId) return null;
  const needsReview = returnedNeedsReview(e);
  if (!needsReview) {
    return {
      label: 'Sheet deleted before it printed',
      title:
        'The sheet this garment was on was deleted or unbuilt before it printed, so no replacement was made. It still needs film, and it is selected by default.',
      needsReview: false,
    };
  }
  if (e.returnedReason === 'ALREADY_REPRINTED' || e.alreadyReprintedOnce === true) {
    return {
      label: 'Already reprinted once — check before printing',
      title:
        'A replacement for this garment was already made. It is back in the queue only because someone reported the same garment again after that sheet was built — which is either a genuinely new defect (the reprint itself was ruined) or a duplicate report of the first one. Printing it again spends another film and another blank, so it is NOT selected by default. Tick it only if the replacement really was ruined.',
      needsReview: true,
    };
  }
  return {
    label: 'Came back — reason unrecorded',
    title:
      'This garment came back to the queue but the reason was not recorded, so it is not known whether a replacement was already made. It is not selected by default; check the sheet it came from before printing it again.',
    needsReview: true,
  };
}

/**
 * ⚠️ THE CROSS-RUN REVIEW RULE — the last gate before two films are spent on one shirt, and after PR-3b
 * the ONLY one for the case that matters.
 *
 * Since the flush groups by (line, RUN), two runs of one order line print side by side instead of being
 * refused. That is correct — a re-batch or a re-send makes two genuinely different sets of shirts — but
 * nothing in the DATA can tell "run A's garment 3 and run B's garment 1 are the same shirt reported
 * twice" from "they are two different shirts". Ranks are allocated per run and restart at 1 after a
 * re-batch, so no rank comparison decides it, and the server therefore refuses only the one case where the
 * identity is CERTAIN (same run, same ordinal, two rows). Everything else is a human judgement, and this
 * is where the human makes it.
 *
 * The rule: when a line has garments in flight from MORE THAN ONE run, the rows of the run that arrived
 * LATER need a deliberate tick. The established run stays pre-selected, so ordinary work is unaffected and
 * the friction lands exactly on the newcomer — the report that might be a duplicate of one already queued.
 *
 * Returns the CONSENT TOKEN MATERIAL for each gated row: the other runs' identities plus whether each has
 * already been committed to film. That second part is load-bearing. A tick given while the sibling run was
 * merely queued is not consent for printing after that sibling's film has been spent, so the token must
 * MOVE when the sibling is flushed — otherwise consent silently survives the event that makes it a
 * duplicate. (Same failure the shipped gate had when consent was a `Set` of ids: it survived a round trip.)
 *
 * ⚠️ Computed over EVERY entry, waiting and on-film alike. A sibling run already on a sheet is the most
 * dangerous kind — its film is spent — and it does not appear in the `waiting` list at all.
 */
export function crossRunReview(entries: readonly ReprintQueueEntry[]): Map<string, string> {
  const byLine = new Map<string, ReprintQueueEntry[]>();
  for (const e of entries) {
    const arr = byLine.get(lineKeyOf(e));
    if (arr) arr.push(e);
    else byLine.set(lineKeyOf(e), [e]);
  }

  const out = new Map<string, string>();
  // ⚠️ THE DURABLE HALF, read first and independent of everything below. A row carrying
  // `crossRunFlushedOnce` has already had a sibling run's film committed, and the derived rule cannot see
  // that once the sibling's rows retire at mark-printed — which is exactly the sequence that spends a
  // second film a day later. The marker is monotone, so the tick is required for good.
  for (const e of entries) {
    if (e.crossRunFlushedOnce === true) out.set(e.entryId, 'spent');
  }
  for (const rows of byLine.values()) {
    // One entry per run: which sheet number it came off, and whether any of its rows is already on film.
    const runs = new Map<string, { seq: number; onFilm: boolean }>();
    for (const e of rows) {
      const key = e.cohortKey ?? '';
      const prev = runs.get(key);
      const onFilm = Boolean(e.flushedIntoBatchId);
      const seq = e.sourceBatchSeq ?? Number.MAX_SAFE_INTEGER;
      if (!prev) runs.set(key, { seq, onFilm });
      else {
        if (seq < prev.seq) prev.seq = seq;
        prev.onFilm = prev.onFilm || onFilm;
      }
    }
    if (runs.size < 2) continue;
    /**
     * The EARLIEST SHEET NUMBER is the established run.
     *
     * ⚠️ Deliberately not the earliest `addedAt`, which was the first version and had a bypass reachable by
     * an ordinary manager action: dismissing the established run's earliest row moves that run's first
     * arrival LATER, which can flip which run counts as established — silently un-gating the newcomer this
     * gate was built to catch, and gating a row that was safe a moment ago. `sourceBatchSeq` is a monotone
     * sheet number frozen on the record at report time, so nothing an operator does can reorder it.
     *
     * `cohortKey` breaks a tie (and covers a row whose sheet number was never captured), so the same queue
     * never gates a different row between two renders — an unstable answer here moves a checkbox under a
     * hand that is reaching for it.
     */
    const ordered = [...runs.entries()].sort(
      (a, b) => a[1].seq - b[1].seq || a[0].localeCompare(b[0]),
    );
    const established = ordered[0]?.[0];
    for (const e of rows) {
      const own = e.cohortKey ?? '';
      if (own === established) continue;
      // The token names every OTHER run and its film state, so it changes when any sibling is flushed,
      // when a third run appears, or when the row itself round-trips (`returnedFromBatchId`, folded in by
      // the page).
      const token = ordered
        .filter(([key]) => key !== own)
        .map(([key, v]) => `${key}:${v.onFilm ? 'F' : '-'}`)
        .join('|');
      // ⚠️ The durable marker WINS. Its token must not be replaced by the derived one, or a row whose
      // sibling film is already spent would re-use consent given while it was merely queued.
      out.set(e.entryId, e.crossRunFlushedOnce === true ? `spent|${token}` : token);
    }
  }
  return out;
}

/** Copy for a cross-run gated row. Says what is known and asks; it must never assert a duplicate. */
export function crossRunBadge(runs: number, spent = false): { label: string; title: string } {
  if (spent) {
    return {
      label: 'Another run of this line was printed — check first',
      title:
        'A reprint sheet has already been built for a DIFFERENT production run of this order line. If that sheet carried this same shirt, printing this row spends another film, another cure cycle and another blank on one garment. Ranks restart when a line is re-batched, so nothing in the data can tell — tick it only if this is genuinely a different shirt.',
    };
  }
  return {
    label: 'Second production run — check before printing',
    title: `This order line already has reprints queued from ${
      runs > 2 ? `${runs - 1} earlier production runs` : 'an earlier production run'
    }. If this is the same shirt reported against both runs, printing it spends another film, another cure cycle and another blank on one garment. Ranks restart when a line is re-batched, so nothing in the data can tell — tick it only if this is genuinely a different shirt.`,
  };
}

export const REASON_LABEL: Record<ReprintQueueEntry['reason'], string> = {
  PRESS_DEFECT: 'Press defect',
  CUT_DAMAGE: 'Cut damage',
  LOST_IN_TRANSIT: 'Lost in transit',
  OTHER: 'Other',
};

export const STATION_LABEL: Record<string, string> = {
  PRESS: 'Heat press',
  CUTTING: 'Cutting bench',
  SHIPPING: 'Shipping',
  OFFICE: 'Office',
};

/**
 * How far the queue has moved since a cost was quoted.
 *
 * The confirm call deliberately sends the FROZEN set the quote was computed from, never a fresh
 * read — otherwise a garment reported by another station while the dialog is open rides onto a
 * sheet whose cost nobody agreed to. (The server's `rev` conditions catch a CHANGED entry but not
 * an ADDED one.) That is the safe behaviour, but silently safe is still surprising, so the dialog
 * says what it is leaving out.
 */
export function quoteDrift(
  quoted: readonly { entryId: string }[],
  waiting: readonly ReprintQueueEntry[],
  /**
   * Rows the review gate is deliberately HOLDING BACK — waiting, but not selectable without a tick.
   *
   * ⚠️ Load-bearing, and the reason this parameter exists at all. Without it every gated row counts as
   * `addedSinceQuote`, and the dialog then renders "1 more garment has been reported since this was
   * priced. It is NOT on this sheet — print again afterwards to include it." BOTH clauses are false — it
   * was not reported since, it was withheld — and the remedy it prescribes is exactly the second film the
   * gate exists to prevent. A held-back row gets its own sentence instead; see `heldBack` below.
   */
  heldBackIds: ReadonlySet<string> = new Set(),
): { addedSinceQuote: number; goneSinceQuote: number; heldBack: number } {
  const inQuote = new Set(quoted.map((q) => q.entryId));
  const live = new Set(waiting.map((e) => e.entryId));
  const notQuoted = waiting.filter((e) => !inQuote.has(e.entryId));
  return {
    addedSinceQuote: notQuoted.filter((e) => !heldBackIds.has(e.entryId)).length,
    goneSinceQuote: quoted.filter((q) => !live.has(q.entryId)).length,
    heldBack: notQuoted.filter((e) => heldBackIds.has(e.entryId)).length,
  };
}
